import { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Send, Mic, MicOff, X, AlertTriangle, Bot, User, Loader2, Globe, MessageCircle, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_TURN_LIMIT = 22;

const OPENING_MESSAGE = {
  role: 'assistant',
  content: "Hello, I'm your 24/7 virtual assistant for P.AI Global Health (Patient AI). You can speak or type. I'll do my best to help using the information available, but I may be limited by what you provide or what I can reliably understand. For personalized medical advice, please consult your healthcare provider. How can I help you today?"
};

const EMERGENCY_911_KEYWORDS = [
  'can\'t breathe', 'chest pain', 'crushing chest', 'unresponsive',
  'not breathing', 'heart attack', 'stroke', 'face drooping',
  'severe bleeding', 'choking', 'seizure', 'unconscious',
  'suicidal', 'kill myself', 'want to die', 'hurting myself',
  'anaphylaxis', 'throat swelling', 'overdose',
];

const GUIDED_PROMPTS = [
  "I have chest pain. Should I go to the ER?",
  "Can you explain my report in simple language?",
  "I feel tired and dizzy. Should I be worried?",
  "What is metformin used for?",
  "What warning signs should I watch for?",
  "What questions should I ask my doctor?",
  "I had surgery recently. What should I watch for?",
  "How do I care for my wound after surgery?",
  "When can I start exercising after surgery?",
  "My family member is caring for me. What should they know?",
  "I feel anxious about my health. What should I do?",
  "\u00bfPuedes explicarlo en espa\u00f1ol?",
  "\u0b8e\u0ba9\u0bcd \u0ba8\u0bbf\u0bb2\u0bc8\u0baf\u0bc8 \u0b8e\u0bb3\u0bbf\u0bae\u0bc8\u0baf\u0bbe\u0b95 \u0bb5\u0bbf\u0bb3\u0b95\u0bcd\u0b95 \u0bae\u0bc1\u0b9f\u0bbf\u0baf\u0bc1\u0bae\u0bbe?",
  "\u0c28\u0c3e \u0c2a\u0c30\u0c3f\u0c38\u0c4d\u0c25\u0c3f\u0c24\u0c3f\u0c28\u0c3f \u0c35\u0c3f\u0c35\u0c30\u0c3f\u0c02\u0c1a\u0c17\u0c32\u0c35\u0c3e?",
  "\u0ca8\u0ca8\u0ccd\u0ca8 \u0cb5\u0cb0\u0ca6\u0cbf\u0caf\u0ca8\u0ccd\u0ca8\u0cc1 \u0cb8\u0cb0\u0cb3\u0cb5\u0cbe\u0c97\u0cbf \u0cb5\u0cbf\u0cb5\u0cb0\u0cbf\u0cb8\u0cbf",
];

const SESSION_LIMIT_MESSAGE = {
  role: 'assistant',
  content: "I've reached the current session limit for now. You can start a new session or continue your care with your healthcare provider."
};

export default function AgentChat({ open, onClose, encounterId }) {
  const [messages, setMessages] = useState([OPENING_MESSAGE]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [language, setLanguage] = useState('English');
  const [listening, setListening] = useState(false);
  const [voiceError, setVoiceError] = useState(null);
  const [turnCount, setTurnCount] = useState(0);
  const [sessionLimited, setSessionLimited] = useState(false);
  const [alertAcknowledged, setAlertAcknowledged] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      const el = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (el) el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (open && encounterId) {
      axios.get(`${API}/agent/history/${encounterId}`).then(res => {
        const hist = res.data.messages || [];
        if (hist.length > 0) {
          setMessages([OPENING_MESSAGE, ...hist.map(m => ({ role: m.role, content: m.content }))]);
          setTurnCount(hist.filter(m => m.role === 'user').length);
        }
      }).catch(() => {});
    }
  }, [open, encounterId]);

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || loading || sessionLimited) return;

    if (turnCount >= SESSION_TURN_LIMIT) {
      setMessages(prev => [...prev, { role: 'user', content: text.trim() }, SESSION_LIMIT_MESSAGE]);
      setSessionLimited(true);
      setInput('');
      return;
    }

    const userMsg = { role: 'user', content: text.trim() };
    // Placeholder assistant bubble we progressively fill: evidence card first
    // (deterministic, ~instant), then the LLM plain-language wording. Tracked by
    // a stable id so concurrent phase updates target the same bubble.
    const pendingId = `a-${Date.now()}`;
    const pendingMsg = { role: 'assistant', content: '', pending: true, id: pendingId };
    setMessages(prev => [...prev, userMsg, pendingMsg]);
    setInput('');
    setLoading(true);
    setTurnCount(prev => prev + 1);

    const payload = { encounterId: encounterId || '', message: text.trim(), language };

    // Phase 1 — deterministic engine evidence appears immediately (no LLM wait).
    // Compute-only preview; /agent/chat below stays authoritative for the reply.
    axios.post(`${API}/agent/chat/evidence-preview`, payload).then(pv => {
      const d = pv.data || {};
      setMessages(prev => prev.map(m => m.id === pendingId ? {
        ...m,
        isEmergency: d.isEmergency,
        isProbing: d.isProbing,
        showEmergencyButton: d.showEmergencyButton,
        evidenceAlerts: d.evidenceAlerts,
        evidenceMeta: d.evidenceMeta,
      } : m));
      if (d.isEmergency || d.showEmergencyButton) setAlertAcknowledged(false);
    }).catch(() => { /* non-blocking — chat response is authoritative */ });

    // Phase 2 — the LLM plain-language wording fills into the SAME bubble.
    try {
      const res = await axios.post(`${API}/agent/chat`, payload);
      setMessages(prev => prev.map(m => m.id === pendingId ? {
        ...m,
        content: res.data.response,
        pending: false,
        isEmergency: res.data.isEmergency,
        isProbing: res.data.isProbing ?? m.isProbing,
        showEmergencyButton: res.data.showEmergencyButton ?? m.showEmergencyButton,
        evidenceAlerts: res.data.evidenceAlerts ?? m.evidenceAlerts,
        evidenceMeta: res.data.evidenceMeta ?? m.evidenceMeta,
      } : m));
      if (res.data.isEmergency || res.data.showEmergencyButton) {
        setAlertAcknowledged(false);
      }
    } catch {
      setMessages(prev => prev.map(m => m.id === pendingId ? {
        ...m,
        pending: false,
        content: "I'm having trouble right now. If this is urgent, please contact your healthcare provider or call 911.",
      } : m));
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  }, [encounterId, language, loading, sessionLimited, turnCount]);

  const startVoice = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setVoiceError('Voice not supported. Please type instead.'); return; }
    setVoiceError(null);
    const recognition = new SR();
    recognition.continuous = false;
    recognition.interimResults = false;
    const langMap = { Spanish: 'es-ES', Hindi: 'hi-IN', Tamil: 'ta-IN', Telugu: 'te-IN', Kannada: 'kn-IN', Creole: 'ht-HT' };
    recognition.lang = langMap[language] || 'en-US';
    setListening(true);
    recognition.onresult = (event) => {
      setListening(false);
      sendMessage(event.results[0][0].transcript);
    };
    recognition.onerror = (event) => {
      setListening(false);
      setVoiceError(event.error === 'not-allowed' ? 'Microphone blocked. Tap lock icon → Allow.' : 'Voice failed. Please type.');
    };
    recognition.onend = () => setListening(false);
    recognition.start();
  }, [language, sendMessage]);

  const handleReset = useCallback(async () => {
    // Truly end the session: clear server-side history so a prior emergency
    // message can never rehydrate and trap the user on reopen (2026-07 fix).
    try {
      await axios.delete(`${API}/agent/history/${encounterId || 'general'}`);
    } catch { /* non-blocking */ }
    setMessages([OPENING_MESSAGE]);
    setInput('');
    setVoiceError(null);
    setTurnCount(0);
    setSessionLimited(false);
    setAlertAcknowledged(false);
  }, [encounterId]);

  const lastAssistant = [...messages].reverse().find(m => m.role === 'assistant');
  const showEmergencyCta = !!(lastAssistant && (lastAssistant.isEmergency || lastAssistant.isProbing || lastAssistant.showEmergencyButton)) && !alertAcknowledged;

  return (
    <Dialog open={open} onOpenChange={(val) => { if (!val) onClose(); }}>
      <DialogContent className="max-w-2xl h-[85vh] flex flex-col p-0 gap-0" data-testid="agent-chat-dialog">
        <DialogTitle className="sr-only">P.AI Global Health — 24/7 Virtual Assistant</DialogTitle>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-white rounded-t-lg">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-full bg-[#2563EB] flex items-center justify-center flex-shrink-0">
              <MessageCircle className="w-4.5 h-4.5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-sm text-slate-900">P.AI Global Health (Patient AI) — 24/7 Virtual Assistant</h3>
              <span className="text-[10px] text-slate-500">Voice + Text · Multilingual · Non-diagnostic</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {encounterId && <Badge variant="outline" className="text-[10px] hidden sm:inline-flex">{encounterId}</Badge>}
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="w-[90px] h-7 text-[10px]" data-testid="agent-language-select">
                <Globe className="w-3 h-3 mr-1" /><SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="English">English</SelectItem>
                <SelectItem value="Spanish">Spanish</SelectItem>
                <SelectItem value="Hindi">Hindi</SelectItem>
                <SelectItem value="Tamil">Tamil</SelectItem>
                <SelectItem value="Telugu">Telugu</SelectItem>
                <SelectItem value="Kannada">Kannada</SelectItem>
                <SelectItem value="Creole">Haitian Creole</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Emergency Banner */}
        <div className="px-4 py-1.5 bg-red-50 border-b border-red-200 text-[10px] text-red-700 flex items-center gap-1.5">
          <AlertTriangle className="w-3 h-3 flex-shrink-0" />
          <span>If experiencing a medical emergency, call 911 immediately. This is not a substitute for emergency services.</span>
        </div>

        {/* Messages */}
        <ScrollArea className="flex-1 px-4 py-3" ref={scrollRef}>
          <div className="space-y-3">
            {messages.map((msg, i) => (
              <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`agent-msg-${i}`}>
                {msg.role === 'assistant' && (
                  <div className="w-6 h-6 rounded-full bg-[#2563EB] flex items-center justify-center flex-shrink-0 mt-1">
                    <Bot className="w-3 h-3 text-white" />
                  </div>
                )}
                <div className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-[#2563EB] text-white rounded-br-md'
                    : msg.isEmergency
                      ? 'bg-red-50 border-2 border-red-300 text-red-900 rounded-bl-md'
                      : 'bg-slate-100 text-slate-800 rounded-bl-md'
                }`}>
                  {msg.role === 'assistant' && msg.evidenceAlerts && msg.evidenceAlerts.length > 0 && (
                    <div className="mb-3 rounded-xl border border-amber-300 bg-amber-50 p-3 text-left" data-testid={`evidence-card-${i}`}>
                      <div className="flex items-center gap-1.5 mb-2">
                        <ShieldCheck className="w-3.5 h-3.5 text-amber-700 flex-shrink-0" />
                        <span className="text-[11px] font-bold text-amber-800 uppercase tracking-wide">Evidence-based alerts · deterministic (no AI guessing)</span>
                      </div>
                      <ul className="space-y-2" data-testid={`evidence-alerts-${i}`}>
                        {msg.evidenceAlerts.map((a, k) => (
                          <li key={k} className="text-xs text-slate-700" data-testid={`evidence-alert-${i}-${k}`}>
                            <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold mr-1.5 align-middle ${
                              (a.severity || '').toUpperCase() === 'CRITICAL' ? 'bg-red-600 text-white'
                                : (a.severity || '').toUpperCase() === 'HIGH' ? 'bg-orange-500 text-white'
                                  : 'bg-amber-400 text-amber-900'
                            }`}>{a.severity}</span>
                            <span className="font-medium">{a.category}</span>
                            {a.message ? <span className="text-slate-600"> — {a.message}</span> : null}
                            {a.source ? <span className="block text-[10px] text-slate-400 mt-0.5">Source: {a.source}</span> : null}
                          </li>
                        ))}
                      </ul>
                      {msg.evidenceMeta && Array.isArray(msg.evidenceMeta.criticalActions) && msg.evidenceMeta.criticalActions.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-amber-200" data-testid={`evidence-actions-${i}`}>
                          <p className="text-[10px] font-bold text-amber-800 uppercase tracking-wide mb-1">What you can do now</p>
                          <ul className="list-disc list-inside space-y-0.5">
                            {msg.evidenceMeta.criticalActions.map((c, k) => (
                              <li key={k} className="text-xs text-slate-700">
                                {typeof c === 'string' ? c : (c.action || c.message || c.recommendation || '')}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <p className="text-[10px] text-slate-500 mt-2 italic">Generated by P.AI deterministic clinical engines (187 symptom + 88 exam/ECG/lab + meta). Not a diagnosis — please review with a clinician.</p>
                    </div>
                  )}
                  {msg.content && msg.content.split('\n').map((line, j) => (
                    <p key={j} className={j > 0 ? 'mt-1.5' : ''}>{line}</p>
                  ))}
                  {msg.role === 'assistant' && msg.pending && (
                    <div className="flex items-center gap-2 text-slate-500" data-testid={`agent-writing-${i}`}>
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-[#2563EB]" />
                      <span className="text-xs italic">Writing a plain-language explanation…</span>
                    </div>
                  )}
                  {msg.isEmergency && (
                    <a
                      href="tel:911"
                      className="mt-3 flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold text-base animate-pulse shadow-lg"
                      data-testid="dial-911-btn"
                    >
                      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
                      DIAL 911 NOW
                    </a>
                  )}
                </div>
                {msg.role === 'user' && (
                  <div className="w-6 h-6 rounded-full bg-slate-300 flex items-center justify-center flex-shrink-0 mt-1">
                    <User className="w-3 h-3 text-slate-600" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </ScrollArea>

        {/* Guided Prompts */}
        {messages.length <= 2 && !loading && (
          <div className="px-4 py-2 border-t border-slate-100 bg-slate-50">
            <p className="text-[10px] font-semibold text-slate-500 mb-1.5">Try asking or saying:</p>
            <div className="flex flex-wrap gap-1.5 max-h-[80px] overflow-y-auto">
              {GUIDED_PROMPTS.map((prompt, i) => (
                <button
                  key={i}
                  onClick={() => setInput(prompt)}
                  className="text-[10px] px-2.5 py-1 rounded-full bg-white border border-slate-200 text-slate-700 hover:border-[#2563EB] hover:text-[#2563EB] transition-colors whitespace-nowrap"
                  data-testid={`guided-prompt-${i}`}
                >
                  {prompt.length > 45 ? prompt.substring(0, 45) + '...' : prompt}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Voice Error */}
        {voiceError && (
          <div className="px-4 py-1.5 bg-amber-50 text-[11px] text-amber-800 flex items-center justify-between">
            <span>{voiceError}</span>
            <button onClick={() => setVoiceError(null)} className="text-amber-600 hover:text-amber-800"><X className="w-3 h-3" /></button>
          </div>
        )}

        {/* Persistent emergency action bar — one-tap 911 during escalation OR
            OPQRST probing, plus an acknowledge control so patients are never
            trapped (2026-07 fix). */}
        {showEmergencyCta && (
          <div className="px-4 py-2.5 bg-red-600 flex items-center justify-between gap-2" data-testid="agent-emergency-bar">
            <a
              href="tel:911"
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-white text-red-700 font-bold text-sm hover:bg-red-50 transition-colors"
              data-testid="agent-call-911-btn"
            >
              <AlertTriangle className="w-4 h-4" /> Call 911 now
            </a>
            <button
              onClick={() => setAlertAcknowledged(true)}
              className="text-[11px] text-white/90 underline hover:text-white"
              data-testid="agent-acknowledge-btn"
            >
              I understand — dismiss
            </button>
          </div>
        )}

        {/* Input */}
        <div className="px-3 py-3 border-t border-slate-200 bg-white rounded-b-lg">
          <div className="flex items-center gap-2">
            <button
              onClick={listening ? () => setListening(false) : startVoice}
              disabled={sessionLimited}
              className={`p-2.5 rounded-full flex-shrink-0 transition-colors ${listening ? 'bg-red-100 text-red-600 animate-pulse' : 'bg-slate-100 text-slate-500 hover:bg-blue-50 hover:text-[#2563EB]'} ${sessionLimited ? 'opacity-50' : ''}`}
              data-testid="agent-voice-btn"
            >
              {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </button>
            <Input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage(input)}
              className="flex-1 bg-slate-50 border-slate-200 rounded-full px-4 h-10 text-sm"
              disabled={loading || sessionLimited}
              data-testid="agent-input"
            />
            <Button
              onClick={() => sendMessage(input)}
              disabled={loading || !input.trim() || sessionLimited}
              className="rounded-full w-10 h-10 p-0 bg-[#2563EB] hover:bg-blue-700 flex-shrink-0"
              data-testid="agent-send-btn"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex items-center justify-between mt-1.5 px-1">
            <span className="text-[9px] text-slate-400">AI assistant · Not medical advice · Clinician review required</span>
            <button onClick={handleReset} className="text-[9px] text-slate-400 hover:text-[#2563EB]">New conversation</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
